import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  TYPOGRAPHY_FONT_PRESETS,
  resolveTypographyConfig,
  resolveTypographySize,
} from '../lib/typography';
import {
  LAYOUT_ELEMENTS,
  resolveLayoutConfig,
  resolveHeroVariant,
} from '../lib/layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsContext = createContext();

/** Decide whether the current URL falls under a typography scope. */
const isInScope = (pathname, scope) => {
  if (scope === 'admin') return pathname.startsWith('/admin');
  if (scope === 'driver') return pathname.startsWith('/driver');
  // storefront = anything that's not admin or driver
  return !pathname.startsWith('/admin') && !pathname.startsWith('/driver');
};

/** Apply CSS variables + Google Fonts link based on the typography config.
 * Called by <TypographyApplier/> which lives INSIDE the Router so it can use
 * react-router's useLocation hook. */
const applyTypography = (typographyRaw, pathname) => {
  const cfg = resolveTypographyConfig(typographyRaw);
  const html = document.documentElement;
  const inScope = (cfg.apply_to || []).some((s) => isInScope(pathname, s));

  if (!inScope) {
    html.classList.remove('typography-scope');
    return;
  }

  const body = TYPOGRAPHY_FONT_PRESETS[cfg.body_font] || TYPOGRAPHY_FONT_PRESETS.system;
  const heading = TYPOGRAPHY_FONT_PRESETS[cfg.heading_font] || TYPOGRAPHY_FONT_PRESETS.system;

  html.style.setProperty('--font-body', body.stack);
  html.style.setProperty('--font-heading', heading.stack);
  html.style.setProperty('--font-scale', String(cfg.font_scale));
  html.style.setProperty('--letter-spacing', `${cfg.letter_spacing}px`);
  // Per-element size CSS variables. Each one falls back to (base × scale)
  // when there's no explicit override — keeping the global scale slider
  // useful as a "scale everything proportionally" shortcut even when
  // some fields have been individually overridden.
  ['h1','h2','h3','body','small','button',
   'product_name','product_size','product_min_qty','product_price','product_uom',
   'category_tile'
  ].forEach((field) => {
    const cssName = field.replace(/_/g, '-');
    // Desktop + mobile sizes both injected. The CSS in `index.css`
    // picks the right one via a media query.
    const remDesktop = resolveTypographySize(cfg, field, 'desktop');
    const remMobile  = resolveTypographySize(cfg, field, 'mobile');
    html.style.setProperty(`--font-size-${cssName}`, `${remDesktop}rem`);
    html.style.setProperty(`--font-size-${cssName}-mobile`, `${remMobile}rem`);

    const wD = cfg.weights?.desktop?.[field];
    const wM = cfg.weights?.mobile?.[field];
    if (wD) html.style.setProperty(`--font-weight-${cssName}`, String(wD));
    else html.style.removeProperty(`--font-weight-${cssName}`);
    if (wM) html.style.setProperty(`--font-weight-${cssName}-mobile`, String(wM));
    else html.style.removeProperty(`--font-weight-${cssName}-mobile`);

    // Italic — track desktop + mobile as separate classes.
    const baseCls = `typo-italic-${cssName}`;
    if (cfg.italics?.desktop?.[field]) html.classList.add(baseCls);
    else html.classList.remove(baseCls);
    const mobCls = `${baseCls}-mobile`;
    if (cfg.italics?.mobile?.[field]) html.classList.add(mobCls);
    else html.classList.remove(mobCls);
  });
  html.classList.add('typography-scope');

  // Inject Google Fonts <link> for any selected families that need it.
  // Singleton link with id="storefront-fonts" — replace href when the set
  // of families changes so we never inject stacked duplicates.
  const families = [body.google, heading.google].filter(Boolean);
  const linkId = 'storefront-fonts';
  let link = document.getElementById(linkId);
  if (families.length === 0) {
    if (link) link.remove();
    return;
  }
  const href = `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join('&')}&display=swap`;
  if (!link) {
    link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
};

/** Apply layout CSS variables based on the layout config + scope.
 * Per-element CSS variables are written on <html> and consumed by the
 * `.layout-*` rules in `index.css`. The hero variant is exposed via two
 * data attributes (`data-hero-variant-desktop`, `data-hero-variant-mobile`)
 * so the hero CSS can switch grid layouts based on device. */
const applyLayout = (layoutRaw, pathname) => {
  const cfg = resolveLayoutConfig(layoutRaw);
  const html = document.documentElement;
  const inScope = (cfg.apply_to || []).some((s) => isInScope(pathname, s));

  if (!inScope) {
    html.classList.remove('layout-scope');
    html.removeAttribute('data-hero-variant-desktop');
    html.removeAttribute('data-hero-variant-mobile');
    return;
  }

  LAYOUT_ELEMENTS.forEach((el) => {
    const e = cfg.elements[el.key];
    const cssBase = el.key.replace(/_/g, '-');
    ['desktop', 'mobile'].forEach((device) => {
      const suffix = device === 'mobile' ? '-mobile' : '';
      const align = e.alignment[device];
      const mw    = e.max_width[device];
      const pt    = e.padding_top[device];
      const pb    = e.padding_bottom[device];
      if (align) html.style.setProperty(`--layout-${cssBase}-align${suffix}`, align);
      else html.style.removeProperty(`--layout-${cssBase}-align${suffix}`);
      if (typeof mw === 'number' && mw > 0) html.style.setProperty(`--layout-${cssBase}-mw${suffix}`, `${mw}rem`);
      else html.style.removeProperty(`--layout-${cssBase}-mw${suffix}`);
      if (typeof pt === 'number' && pt >= 0) html.style.setProperty(`--layout-${cssBase}-pt${suffix}`, `${pt}rem`);
      else html.style.removeProperty(`--layout-${cssBase}-pt${suffix}`);
      if (typeof pb === 'number' && pb >= 0) html.style.setProperty(`--layout-${cssBase}-pb${suffix}`, `${pb}rem`);
      else html.style.removeProperty(`--layout-${cssBase}-pb${suffix}`);
    });
  });

  html.setAttribute('data-hero-variant-desktop', resolveHeroVariant(cfg, 'desktop'));
  html.setAttribute('data-hero-variant-mobile', resolveHeroVariant(cfg, 'mobile'));

  // Hero BLOCK size — applied to the .layout-hero-text-col blue panel.
  // Each property has a desktop & mobile variant; CSS in index.css picks the
  // right one via @media. Default (null) preserves the existing aspect-square.
  ['desktop', 'mobile'].forEach((device) => {
    const suffix = device === 'mobile' ? '-mobile' : '';
    const ar = cfg.hero_block.aspect_ratio[device];
    const h  = cfg.hero_block.height[device];
    const mw = cfg.hero_block.max_width[device];
    const ip = cfg.hero_block.inner_padding[device];
    if (ar && ar !== 'auto') {
      // Stored as "1/1", "16/9" etc → CSS aspect-ratio expects "1 / 1".
      html.style.setProperty(`--layout-hero-block-aspect${suffix}`, ar.replace('/', ' / '));
    } else if (ar === 'auto') {
      html.style.setProperty(`--layout-hero-block-aspect${suffix}`, 'auto');
    } else {
      html.style.removeProperty(`--layout-hero-block-aspect${suffix}`);
    }
    if (typeof h === 'number' && h > 0) html.style.setProperty(`--layout-hero-block-height${suffix}`, `${h}rem`);
    else html.style.removeProperty(`--layout-hero-block-height${suffix}`);
    if (typeof mw === 'number' && mw > 0) html.style.setProperty(`--layout-hero-block-mw${suffix}`, `${mw}rem`);
    else html.style.removeProperty(`--layout-hero-block-mw${suffix}`);
    if (typeof ip === 'number' && ip >= 0) html.style.setProperty(`--layout-hero-block-ip${suffix}`, `${ip}rem`);
    else html.style.removeProperty(`--layout-hero-block-ip${suffix}`);
  });

  html.classList.add('layout-scope');
};


export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings(data);
    } catch (e) {
      console.error('Failed to load settings', e);
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refresh: fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

/** Mount this component INSIDE a <Router> so it can react to navigation
 * and re-apply the typography (e.g. when the admin clicks a link into
 * /admin/* the storefront fonts should turn off). It renders nothing. */
export const TypographyApplier = () => {
  const { settings } = useContext(SettingsContext);
  const location = useLocation();
  useEffect(() => {
    if (settings) {
      applyTypography(settings.typography, location.pathname);
      applyLayout(settings.layout, location.pathname);
    }
  }, [settings, location.pathname]);
  return null;
};

export const useSettings = () => useContext(SettingsContext);
